import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { fetchHospitals, Hospital } from '../../store/hospitalSlice';
import { AppDispatch, RootState } from '../../store/store';
import { createUser, fetchUsers, updateUser, User } from '../../store/usersSlice';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import { passwordValidationSchema } from '../ui/DaliaComponents';

const RoleEnum = ['DOCTOR', 'ARC', 'ADMIN'];

const HealthProfessional: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { hospitals } = useSelector((state: RootState) => state.hospitals);
  const { users, loading, error } = useSelector((state: RootState) => state.users);

  const [show, setShow] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const validationSchema = Yup.object({
    email: Yup.string().email("Format de l'email invalide").required('Requis'),
    firstName: Yup.string().required('Requis'),
    lastName: Yup.string().required('Requis'),
    role: Yup.string().oneOf(RoleEnum, 'Rôle invalide').required('Requis'),
    hospital: Yup.string()
      .nullable()
      .when('role', {
        is: (role: string) => role === 'DOCTOR' || role === 'ARC',
        then: (schema) => schema.required("L'hopital est requis pour les roles ARC et DOCTOR"),
        otherwise: (schema) => schema.nullable(),
      }),
    password: showPwd ? passwordValidationSchema : Yup.string(),
  });

  const formik = useFormik<{
    id: string | undefined;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    password: string;
    hospital: string;
  }>({
    initialValues: {
      id: undefined,
      email: '',
      firstName: '',
      lastName: '',
      role: RoleEnum[0],
      password: '',
      hospital: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const hospital = hospitals.find((hospital) => hospital.id === values.hospital);
      const user = { ...values, hospital: hospital };
      if (values.id) {
        void dispatch(updateUser(user));
      } else {
        void dispatch(createUser(user));
      }
      handleClose();
    },
  });

  useEffect(() => {
    void dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    void dispatch(fetchHospitals());
  }, [dispatch]);

  const handleShow = () => {
    setShow(true);
  };

  const handleCreate = () => {
    formik.resetForm();
    setShowPwd(true);
    handleShow();
  };

  const handleEdit = (user: User) => {
    void formik.setValues({
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      password: '',
      hospital: user.hospital ? user.hospital.id : '',
    });
    handleShow();
    setShowPwd(false);
  };

  const handleClose = () => {
    setShowPwd(false);
    setShow(false);
  };

  const columns: GridColDef<User>[] = [
    { field: 'firstName', headerName: 'Prénom', width: 150 },
    { field: 'lastName', headerName: 'Nom', width: 150 },
    { field: 'hospital', headerName: 'Centre', width: 200, valueGetter: (params: Hospital) => (params ? params.name : '') },
    { field: 'role', headerName: 'Rôle', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    {
      field: 'action',
      headerName: 'Action',
      width: 150,
      renderCell: (params: GridRenderCellParams<User>) => (
        <Button
          color="warning"
          onClick={() => {
            handleEdit(params.row);
          }}
          variant="contained"
        >
          Modifier
        </Button>
      ),
    },
  ];

  return (
    <Container disableGutters sx={{ flex: 1, flexDirection: 'column', p: 0, height: '100vh' }}>
      <Typography component="h1" gutterBottom sx={{ mt: 4 }} variant="h4">
        Professionnels de santé
      </Typography>

      {loading && <CircularProgress />}
      <Button onClick={handleCreate} variant="contained">
        Creer un PDS
      </Button>
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ mt: 4, flexGrow: 1, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 300px)' }}>
        <DataGrid
          autoPageSize
          columns={columns}
          disableColumnFilter
          onRowClick={(params) => {
            handleEdit(params.row as User);
          }}
          pagination
          paginationMode="client"
          rows={users}
          sx={{
            flex: 1,
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer',
            },
            height: '100%',
          }}
        />
      </Box>

      <Dialog onClose={handleClose} open={show}>
        <DialogTitle>{formik.values.id ? 'Edit PDSs' : 'Create PDS'}</DialogTitle>
        <DialogContent>
          <Box autoComplete="off" component="form" noValidate onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              id="email"
              label="Email"
              margin="normal"
              placeholder="Entrer l'email"
              type="email"
              {...formik.getFieldProps('email')}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              required
            />
            <TextField
              fullWidth
              label="Prénom"
              margin="normal"
              placeholder="Entrer le prénom"
              type="text"
              {...formik.getFieldProps('firstName')}
              error={formik.touched.firstName && Boolean(formik.errors.firstName)}
              helperText={formik.touched.firstName && formik.errors.firstName}
              required
            />
            <TextField
              fullWidth
              label="Nom"
              margin="normal"
              placeholder="Entrer le nom"
              type="text"
              {...formik.getFieldProps('lastName')}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
              required
            />
            <InputLabel id="role-label">Role</InputLabel>
            <Select fullWidth labelId="role-label" {...formik.getFieldProps('role')} error={formik.touched.role && Boolean(formik.errors.role)}>
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="ARC">ARC</MenuItem>
              <MenuItem value="DOCTOR">Doctor</MenuItem>
            </Select>
            {formik.touched.role && formik.errors.role && <div>{formik.errors.role}</div>}
            <InputLabel>Centre investigateur</InputLabel>
            <Select fullWidth {...formik.getFieldProps('hospital')} error={formik.touched.hospital && Boolean(formik.errors.hospital)}>
              {hospitals.map((hospital) => (
                <MenuItem key={hospital.id} value={hospital.id}>
                  {hospital.name}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.hospital && formik.errors.hospital && <div>{formik.errors.hospital}</div>}
            {showPwd && (
              <TextField
                fullWidth
                label="Password"
                margin="normal"
                placeholder="Enter user password"
                required
                type="password"
                {...formik.getFieldProps('password')}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
              />
            )}
            <DialogActions>
              <Button color="primary" onClick={handleClose}>
                Close{' '}
              </Button>
              <Button color="primary" type="submit" variant="contained">
                Save Changes
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default HealthProfessional;
